// src/components/PricingCards.js
import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { TokenContext } from "./tokenContext";
import {
  Box, Card, CardContent, CardActions, Button, Chip,
  Grid, Stack, Typography, Divider
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import MicIcon from "@mui/icons-material/Mic";
import CheckIcon from "@mui/icons-material/Check";
import "./PricingCards.css";
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { loadStripe } from '@stripe/stripe-js';

const features = {
  tokensOnly: [
    "35 tokens to spend across tools",
    "Locked room entry (2 tokens), AI chat (1), tasks (1), plans (1)",
    "Join open community rooms (mic time not guaranteed)",
  ],
  growth: [
    "Monthly mini-assessment (10m) + 3 targeted tasks",
    "Priority Unmute · Tier-1 (top ~10% in queue)",
    "1 guaranteed Mic Slot/week in micro-rooms (cap ≤12)",
    "1 Harkness seat/month (cap ≤10) with participation map",
    "Monthly participation report-card (lite)",
  ],
  serious: [
    "Monthly full assessment (20m) + 4-week micro-plan",
    "Priority Unmute · Tier-2 (top ~5% in queue)",
    "2 guaranteed Mic Slots/week in micro-rooms (cap ≤10)",
    "Weekly Harkness seat (cap ≤10) + map & commentary",
    "1 portfolio clip/month (60–90s edited with tags)",
    "Podcast audition window (quarterly)",
  ],
  pro: [
    "Monthly performance review (30m) + weekly 10m check-ins",
    "Priority Unmute · Tier-3 (top ~2% in queue)",
    "4 guaranteed Mic Slots/week in intensives (cap ≤6)",
    "2× weekly Harkness seats + trend mapping",
    "Podcast segment production (quarterly, quality-bar, up to 300s)",
    "Exam / interview simulation (monthly)",
  ],
};

const plans = [
  {
    name: "Starter",
    price: 35,
    tokens: 35,
    serviceNote: "Tokens-only, unused roll over monthly",
    hoursRange: "~4–6 hrs typical/month",
    bestFor: "Try it out · flexible self-practice",
    perks: features.tokensOnly,
    cta: "Start with Starter",
    accent: "linear-gradient(90deg,#525252,#262626)",
    Icon: AccessTimeIcon,
    popular: false,
  },
  {
    name: "Growth",
    price: 100,
    tokens: 70,
    serviceNote: "+ 30% coached services",
    hoursRange: "~12–18 hrs typical/month",
    bestFor: "Consistent progress · weekly speaking",
    perks: features.growth,
    cta: "Choose Growth",
    accent: "linear-gradient(90deg,#2563eb,#4338ca)",
    Icon: RocketLaunchIcon,
    popular: true,
  },
  {
    name: "Serious",
    price: 150,
    tokens: 105,
    serviceNote: "+ 30% coached services",
    hoursRange: "~18–27 hrs typical/month",
    bestFor: "Portfolio-ready speech · steady coaching",
    perks: features.serious,
    cta: "Go Serious",
    accent: "linear-gradient(90deg,#c026d3,#db2777)",
    Icon: StarIcon,
    popular: false,
  },
  {
    name: "Pro",
    price: 300,
    tokens: 210,
    serviceNote: "+ 30% coached services",
    hoursRange: "~36–54 hrs typical/month",
    bestFor: "Fast-track outcomes · interview/IELTS focus",
    perks: features.pro,
    cta: "Train Pro",
    accent: "linear-gradient(90deg,#059669,#0d9488)",
    Icon: VerifiedIcon,
    popular: false,
  },
];
const stripePromise = loadStripe(process.env.REACT_APP_STP_REAL); // publishable test key

async function startPlanCheckout(planKey, onGotoTokens) {
  const stripe = await stripePromise;
  const user = getAuth().currentUser;
  const idToken = await user.getIdToken();

  // 1) Fetch user doc for role / plan gate
  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    alert("Account not initialized yet—please re-login.");
    return;
  }
  const u = snap.data();
 
  // 2) Block admins
  if (u.role === "admin") {
    alert("Admins cannot purchase plans.");
    return;
  }
 
  // 3) Block if an active plan exists
  const expiresAt = u?.planInfo?.expiresAt?.toDate?.();
  const activePlan = u?.planInfo?.status === "active" && expiresAt && expiresAt > new Date();
  if (activePlan) {
    const dayAfter = new Date(expiresAt.getTime() + 3*60*60*1000);
    const nice = dayAfter.toLocaleDateString();
    const needsTokens = window.confirm(
      `You already have an active ${u.planInfo.key} plan until ${expiresAt.toLocaleDateString()}.\n\n` +
      `If you only need more tokens, go to the token purchase section now?\n\n` +
      `You can buy a new plan on or after ${nice}.`
    );
    if (needsTokens) {
      // send them to your tokens view
      if (typeof onGotoTokens === "function") onGotoTokens();
      else {
        document.getElementById("tokens-section")?.scrollIntoView({ behavior: "smooth" }) ||
        (window.location.hash = "#tokens-section");
      }

    }
    return;
  }


  const res = await fetch('https://fluency.ispeakwell.ca/api/create-plan-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      planKey,
      frontendOrigin: window.location.origin, // e.g., https://localhost:3000
    }),
  });

  if (!res.ok) {
    // Server returns structured info (see server section below)
    const j = await res.json().catch(() => ({}));
    if (j?.reason === "admin") {
      alert("Admins cannot purchase plans.");
      return;
    }
    if (j?.reason === "active_plan") {
      const nice = j?.dayAfter || "(later)";
      const needsTokens = window.confirm(
        `You already have an active ${j.key} plan.\n\n` +
        `You can buy a new plan on or after ${nice}.\n\n` +
        `Need more tokens right now?`
      );
       if (needsTokens) {
         if (typeof onGotoTokens === "function") onGotoTokens();
         else {
           document.getElementById("tokens-section")?.scrollIntoView({ behavior: "smooth" }) ||
           (window.location.hash = "#tokens-section");
         }
       }
      return;
    }
    throw new Error(`Checkout create failed: ${res.status}`);  
 }
 const { sessionId } = await res.json();
 await stripe.redirectToCheckout({ sessionId });
}

function PlanCard({ plan, onGotoTokens, onSelect }) {
  const { Icon } = plan;
  return (
    <Card
    variant="outlined"
    elevation={0}
    className="plan-card"
    style={{ '--accent': plan.accent }}
    >
      {/* Accent blur strip */}
      <Box className="accent-strip" />

      {/* Popular badge */}
      {plan.popular && (
        <Chip
          label="Most Popular"
          color="primary"
          size="small"
          className="badge-popular"
        />
      )}

      <Box className="plan-body">
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} className="plan-head">
          <Stack direction="row" spacing={1.25} alignItems="center" className="plan-title">
            <Icon fontSize="small" className="title-icon" />
            <Typography variant="h6" className="title-text">
              {plan.name}
            </Typography>
          </Stack>
          <Box className="price-box">
            <Typography variant="h4" className="price">
              ${plan.price}
            </Typography>
          {plan.name !== "Starter" && (
              <Typography variant="caption" className="price-sub">
                one-month plan
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} alignItems="center" className="tokens-row">
          <Typography variant="body2" className="tokens-strong">
            {plan.tokens} tokens
          </Typography>
          <Typography variant="body2" className="tokens-note">
            {plan.serviceNote}
          </Typography>
        </Stack>
      </Box>

      <Divider className="divider" />

      <CardContent className="plan-content">
        <Stack direction="row" spacing={1} alignItems="center" className="hours-row">
          <MicIcon fontSize="small" className="hours-icon" />
          <Typography variant="body2" className="hours-text">
            {plan.hoursRange}
          </Typography>
        </Stack>

        <Stack component="ul" spacing={1} className="perks-list">
          {plan.perks.map((p, i) => (
            <Stack key={i} component="li" direction="row" spacing={1} alignItems="flex-start" className="perk-item">
              <CheckIcon fontSize="small" className="perk-check" />
              <Typography variant="body2" className="perk-text">{p}</Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>

      <CardActions className="plan-actions">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          className="actions-row"
        >
          <Button size="large" variant="contained" disableElevation className="btn-cta" onClick={onSelect}>
            {plan.cta}
          </Button>
          <Box className="legal">
            <Typography variant="caption" className="legal-line">
              <strong>Tokens roll over</strong> (balance cap applies). Unused coached services expire monthly. Resubscribe monthly to continue plan.
            </Typography>
            <Typography variant="caption" className="legal-line">
              Priority is a tiebreaker; open rooms remain available to all.
            </Typography>
          </Box>
        </Stack>
      </CardActions>
    </Card>
  );
}

export default function PricingCards({ onGotoTokens }) {
  const navigate = useNavigate();
  const { setPlanIntent } = useContext(TokenContext);

  const handlePlanCTA = async (planName) => {
    const user = getAuth().currentUser;
    if (!user) {
      setPlanIntent(planName);
      navigate("/login");
      return;
    }
    await startPlanCheckout(planName, onGotoTokens);
  };
  return (
    <Box className="pricing-container">
      <Box className="pricing-header">
        <Typography variant="h4" className="pricing-title">
          Choose your iSpeakWell plan
        </Typography>
        <Typography variant="body2" className="pricing-sub">
          Train your voice, build confidence, and increase your real speaking time—everyday.
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, md: 3 }}>
        {plans.map((plan) => (
          <Grid key={plan.name} item xs={12} md={6} xl={3}>
            <PlanCard
              plan={plan}
              onGotoTokens={onGotoTokens}
              onSelect={() => handlePlanCTA(plan.name)}
            />
          </Grid>
        ))}
      </Grid>

      <Box className="footnotes">
        <Stack spacing={0.75}>
          <Typography variant="body2" className="footnote">
            <strong>How tokens work:</strong> Locked room entry costs 2 tokens; AI chat, plan tasks, and AI-generated speech plans typically cost 1 token; select bundles cost 35 tokens and up.
          </Typography>
          <Typography variant="body2" className="footnote">
            <strong>Outcome ranges:</strong> Hours shown reflect typical combined speaking from micro-rooms, Harkness seats, and AI voice practice; actual results vary by participation, room size, and scheduling.
          </Typography>
          <Typography variant="body2" className="footnote">
            <strong>Fair access:</strong> Priority affects queue order inside coached rooms but never blocks open-room participation.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
